/**
 * @fileoverview Connect workspace tab identity to shareable URL state. The router owns navigation history; the workspace supplies supported tab identities. Alias normalization preserves old deep links and unrelated query values. `AppTabView.js` consumes the controlled index boundary without owning URL persistence.
 */

import { useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

const EMPTY_ALIASES = {}

/**
 * Supported tab IDs must be nonempty. Alias normalization replaces history; ordinary changes use router navigation options while preserving unrelated query values and location state. onTabChange accepts {index}; setActiveIndex accepts an index.
 */
function useUrlTabState(tabIds, options = {}) {
    const { paramName = 'tab', defaultTabId = tabIds[0], aliases = EMPTY_ALIASES } = options
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const fallbackTabId = tabIds.includes(defaultTabId) ? defaultTabId : tabIds[0]
    const requestedTabId = searchParams.get(paramName)
    const aliasedTabId = requestedTabId ? aliases[requestedTabId] : null
    const canonicalTabId = tabIds.includes(requestedTabId)
        ? requestedTabId
        : (aliasedTabId && tabIds.includes(aliasedTabId) ? aliasedTabId : null)
    const activeTabId = canonicalTabId || fallbackTabId
    const activeIndex = Math.max(tabIds.indexOf(activeTabId), 0)

    useEffect(

    () => {
        if (!requestedTabId || requestedTabId === activeTabId) {
            return
        }

        if (aliases[requestedTabId] !== activeTabId) {
            return
        }

        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.set(paramName, activeTabId)

        if (nextSearchParams.toString() === searchParams.toString()) {
            return
        }

        setSearchParams(nextSearchParams, {
            replace: true,
            state: location.state,
        })
    }, [activeTabId, aliases, location.state, paramName, requestedTabId, searchParams, setSearchParams])

    const setActiveIndex =

    (index, navigateOptions = {}) => {
        const nextTabId = tabIds[index] || fallbackTabId

        if (!nextTabId) {
            return
        }

        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.set(paramName, nextTabId)

        if (nextSearchParams.toString() === searchParams.toString()) {
            return
        }

        setSearchParams(nextSearchParams, {
            ...navigateOptions,
            state: location.state,
        })
    }

    return {
        activeIndex,
        activeTabId,
        onTabChange: ({ index }) => setActiveIndex(index),
        setActiveIndex,
    }
}

export default useUrlTabState
